import { useEffect, useMemo, useRef, useState } from "react";
import {
  RegisterOptions,
  SubmitHandler,
  useForm,
  useFormState,
  useWatch,
} from "react-hook-form";
import { isAfter, parseISO } from "date-fns";

import {
  Field,
  FieldHelper,
  FieldLabel,
  FormCard,
  FormPanel,
  LabelRow,
  ToneSegments,
} from "../../../../components/FormPanel";
import {
  FormInput,
  FormMoneyInput,
  InputDate,
  SelectForm,
} from "../../../../components/Input";
import { buildDraftDetails } from "../../../../components/PopUp/confirmShared";
import { useGetAccountsQuery } from "../../../../services";
import {
  DateFormat,
  localToday,
  formatCurrency,
  formatDate,
} from "../../../../utils";
import { FUNDING_ACCOUNT_TYPES } from "../../../Accounts/types";
import {
  tradeDateFloor,
  validateAmount,
  validateTradeDate,
} from "../tradeValidation";
import { MobileBrokerageTransferForm } from "./MobileBrokerageTransferForm";
import {
  createDefaultBrokerageTransfer,
  DIRECTION_SEGMENTS,
  FORM_ID,
} from "./constants";
import {
  BrokerageTransferDirection,
  BrokerageTransferFormValues,
  BrokerageTransferPanelProps,
  ExternalAccountOption,
} from "./types";
import { TransferSummary } from "./styles";

/**
 * The holdings-ledger side of a transfer is written by the API as cash events,
 * but the form is a normal transaction form: an external bank endpoint is
 * always mandatory and no retired funding-account field is involved.
 */
export const BrokerageTransferPanel = ({
  isOpen,
  onClose,
  mode = "create",
  initialValues = null,
  investmentName,
  investmentAccountId,
  investmentStartDate,
  handleOnSubmit,
}: BrokerageTransferPanelProps) => {
  const [panelError, setPanelError] = useState<string | null>(null);
  // An edit whose other endpoint this form cannot represent. The banner alone
  // would not stop the user picking a funding account and saving, and that
  // `PUT` re-creates the transfer against the account they picked — silently
  // repointing it, which is exactly what the banner says cannot happen here.
  const [isCounterpartyUnsupported, setIsCounterpartyUnsupported] =
    useState(false);
  const { data: accounts = [] } = useGetAccountsQuery();

  const form = useForm<BrokerageTransferFormValues>({
    defaultValues: createDefaultBrokerageTransfer(),
    mode: "onBlur",
    reValidateMode: "onChange",
  });
  const { control, getValues, reset, setError, setValue, trigger } = form;
  const { isSubmitting } = useFormState({ control });
  const direction = useWatch({ control, name: "direction" });
  const enteredAmount = useWatch({ control, name: "amountEur" });
  const date = useWatch({ control, name: "date" });
  const selectedExternalAccount = useWatch({
    control,
    name: "externalAccount",
  });

  const externalAccountOptions = useMemo<ExternalAccountOption[]>(
    () =>
      accounts
        .filter(
          (account) =>
            FUNDING_ACCOUNT_TYPES.includes(account.accountType) &&
            account.id !== investmentAccountId,
        )
        .map((account) => ({
          label: account.name,
          value: String(account.id),
          startDate: account.startDate,
        })),
    [accounts, investmentAccountId],
  );

  const isEditing = mode === "edit";

  // Which transfer the form currently holds. The prefill must run once per
  // opened row and never again: `externalAccountOptions` is derived from
  // `useGetAccountsQuery`, whose data is a fresh array identity on every
  // refetch, and `transactionRipple` invalidates `Account` bare — so any
  // transaction write elsewhere in the app would otherwise re-`reset()` the
  // form and discard whatever the user had typed.
  const prefilledTransferId = useRef<number | null>(null);

  // The list projection intentionally carries no note or endpoint dates. On an
  // edit the page fetches the transaction, then this panel resolves its saved
  // external-account id back to the current option so its start-date rule is
  // applied to the prefilled form too.
  useEffect(() => {
    if (!isOpen) {
      prefilledTransferId.current = null;
      setIsCounterpartyUnsupported(false);
      return;
    }
    if (!isEditing || !initialValues) {
      return;
    }
    if (prefilledTransferId.current === initialValues.transferId) {
      return;
    }
    const externalAccount = externalAccountOptions.find(
      (option) => Number(option.value) === initialValues.externalAccountId,
    );
    if (!externalAccount) {
      // A transfer's other endpoint can be any account the API accepts, while
      // this form only offers funding accounts — a broker/savings transfer is
      // valid and editable but has no option to select. Say so rather than
      // silently rendering a blank "Edit cash transfer" form.
      if (accounts.length > 0) {
        prefilledTransferId.current = initialValues.transferId;
        setIsCounterpartyUnsupported(true);
        setPanelError(
          "This transfer's other account can't be edited here. Change it from the transactions page instead.",
        );
      }
      return;
    }
    prefilledTransferId.current = initialValues.transferId;
    reset({
      direction: initialValues.direction,
      amountEur: initialValues.amountEur,
      date: initialValues.date,
      externalAccount,
      note: initialValues.note,
    });
    setIsCounterpartyUnsupported(false);
    setPanelError(null);
  }, [
    accounts.length,
    externalAccountOptions,
    initialValues,
    isEditing,
    isOpen,
    reset,
  ]);

  const investmentDateFloor = useMemo(
    () => tradeDateFloor(investmentStartDate),
    [investmentStartDate],
  );
  const externalDateFloor = selectedExternalAccount
    ? parseISO(selectedExternalAccount.startDate)
    : investmentDateFloor;
  const minDate = isAfter(externalDateFloor, investmentDateFloor)
    ? externalDateFloor
    : investmentDateFloor;
  const dateFloorAccountName = isAfter(externalDateFloor, investmentDateFloor)
    ? selectedExternalAccount?.label
    : investmentName;

  // A later-opened bank account must invalidate an otherwise valid entered
  // date immediately, rather than waiting for a failed POST.
  useEffect(() => {
    if (selectedExternalAccount) {
      void trigger("date");
    }
  }, [selectedExternalAccount?.value, trigger]);

  const transferSummary = useMemo(() => {
    if (
      !selectedExternalAccount ||
      typeof enteredAmount !== "number" ||
      !Number.isFinite(enteredAmount) ||
      enteredAmount <= 0
    ) {
      return null;
    }
    const amount = formatCurrency(enteredAmount);
    const formattedDate = formatDate(date, DateFormat.DMY_SPACE);
    const source =
      direction === "DEPOSIT" ? selectedExternalAccount.label : investmentName;
    const destination =
      direction === "DEPOSIT" ? investmentName : selectedExternalAccount.label;
    const destinationPhrase =
      direction === "DEPOSIT"
        ? `as cash in ${destination}`
        : `in ${destination}`;
    return `${amount} leaves ${source} and arrives ${destinationPhrase} on ${formattedDate}. One movement, both sides recorded together.`;
  }, [date, direction, enteredAmount, investmentName, selectedExternalAccount]);

  /** Short enough to sit beside the field count in the discard dialog. */
  const directionLabel =
    direction === "DEPOSIT"
      ? `Deposit into ${investmentName}`
      : `Withdrawal from ${investmentName}`;

  const handleDirectionChange = (next: BrokerageTransferDirection) => {
    setValue("direction", next, { shouldDirty: true });
    setPanelError(null);
  };

  // Every entry field, but never Cancel: leaving must stay available in the blocked state.
  const isDisabled = isSubmitting || isCounterpartyUnsupported;

  const onSubmit: SubmitHandler<BrokerageTransferFormValues> = (data) => {
    setPanelError(null);
    return handleOnSubmit(data, { setError, setPanelError });
  };

  const dateRules: RegisterOptions<BrokerageTransferFormValues, "date"> = {
    required: "Date is required!",
    // A transfer is bounded by *two* start dates, so the floor breach names the
    // account that produced it. The comparison itself stays in
    // `validateTradeDate` — the label is the only thing this panel adds.
    validate: (value: Date) =>
      validateTradeDate(value, minDate, dateFloorAccountName),
  };

  const eyebrow = isEditing ? "EDIT TRANSFER" : "MOVE CASH";
  const title = isEditing
    ? `Edit cash transfer for ${investmentName}`
    : `Move cash to or from ${investmentName}`;
  const submitLabel = isEditing ? "Save transfer" : "Move cash";
  const discardTitle = isEditing
    ? "Cancel this transfer edit?"
    : "Cancel this cash movement?";
  const mobileTitle = isEditing ? "Edit cash transfer" : "Move cash";
  const mobileSubmitLabel = isEditing ? "Save" : "Move";

  // Only user-entered fields: `direction` and `date` always carry defaults, so counting them would report a filled form for an untouched one.
  const buildDiscardDetails = () => {
    const values = getValues();
    return buildDraftDetails({
      noun: "cash movement",
      fieldValues: [
        values.amountEur,
        values.externalAccount?.value,
        values.note,
      ],
      amount:
        typeof values.amountEur === "number"
          ? formatCurrency(values.amountEur)
          : undefined,
      primaryLabel: directionLabel,
    });
  };

  return (
    <FormPanel
      open={isOpen}
      onClose={onClose}
      error={panelError}
      ariaLabel="Move brokerage cash form"
      eyebrow={eyebrow}
      title={title}
      form={form}
      formId={FORM_ID}
      onSubmit={onSubmit}
      submitLabel={submitLabel}
      isSubmitDisabled={isCounterpartyUnsupported}
      discard={{
        title: discardTitle,
        confirmLabel: "Discard",
        details: buildDiscardDetails,
      }}
      mobile={{
        title: mobileTitle,
        submitLabel: mobileSubmitLabel,
        fields: (
          <MobileBrokerageTransferForm
            control={control}
            direction={direction}
            externalAccountOptions={externalAccountOptions}
            minDate={minDate}
            dateRules={dateRules}
            transferSummary={transferSummary}
            isDisabled={isDisabled}
            onDirectionChange={handleDirectionChange}
          />
        ),
      }}
    >
      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel>Direction *</FieldLabel>
          </LabelRow>
          <ToneSegments
            groupLabel="Transfer direction"
            options={DIRECTION_SEGMENTS}
            selectedValue={direction}
            disabled={isDisabled}
            onChange={(segment) => handleDirectionChange(segment.value)}
          />
        </Field>

        <Field>
          <LabelRow>
            <FieldLabel htmlFor="brokerage-transfer-account">
              External account *
            </FieldLabel>
          </LabelRow>
          <SelectForm
            id="brokerage-transfer-account"
            name="externalAccount"
            control={control}
            rules={{ required: "External account is required!" }}
            defaultValue={null}
            options={externalAccountOptions}
            disabled={isDisabled}
          />
          <FieldHelper>Choose the bank account at the other end.</FieldHelper>
        </Field>
      </FormCard>

      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel htmlFor="brokerage-transfer-amount">
              Amount *
            </FieldLabel>
          </LabelRow>
          <FormMoneyInput
            id="brokerage-transfer-amount"
            required
            name="amountEur"
            control={control}
            rules={{ validate: validateAmount }}
            disabled={isDisabled}
            placeholder="0.00"
          />
          {transferSummary && (
            <TransferSummary>{transferSummary}</TransferSummary>
          )}
        </Field>
      </FormCard>

      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel htmlFor="brokerage-transfer-date">Date *</FieldLabel>
          </LabelRow>
          <InputDate
            id="brokerage-transfer-date"
            name="date"
            control={control}
            rules={dateRules}
            disabled={isDisabled}
            minDate={minDate}
            maxDate={localToday()}
            placeholder="YYYY/MM/DD"
          />
          <FieldHelper>
            Must be on or after both accounts' start dates.
          </FieldHelper>
        </Field>

        <Field>
          <LabelRow>
            <FieldLabel htmlFor="brokerage-transfer-note">Note</FieldLabel>
          </LabelRow>
          <FormInput
            id="brokerage-transfer-note"
            name="note"
            control={control}
            disabled={isDisabled}
            placeholder="Optional"
          />
        </Field>
      </FormCard>
    </FormPanel>
  );
};
